// ─── 🎓 «Listino prezzi lezioni» (voce 19, tappa 19b) ────────────────────────
//
// 🗣️ Dal mockup approvato (`mockup/listino-lezioni-mockup.html`): quanto paga
// **ogni allievo**, per lezione, allenamento o corso. Tre linguette, una
// colonna ciascuna — la durata la dice il tipo (⇒ `DURATA_DEL_TIPO`).
//
// ⭐ **Si salva uscendo dalla casella**, una casella alla volta: niente bottone
//    «Salva» da ricordarsi, e ogni cifra finisce nel registro col suo prima e
//    dopo. **Vuota** vuol dire «prezzo non deciso», lo **zero** vuol dire gratis.
// ✍️ Si scrive **solo qui**: l'app dei maestri le legge e basta (tappa 19d).
// 🆕 20/10/2026 (mockup «Partita tattica» v2): la quarta linguetta, «Partita tattica», con una
//    riga per maestro; e l'allenamento con la sola riga da quattro.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DURATA_DEL_TIPO,
  ETICHETTE_LIVELLO,
  ETICHETTE_TIPO_TARIFFA,
  TIPI_TARIFFA,
  prezzoDellaVoce,
  vociDelTipo,
  type ScriviTariffa,
  type TariffeLezioni,
  type MaestroDiSegreteria,
  type TipoTariffa,
  type VoceTariffa,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { centesimi } from '../../features/listini/prezzo.js';
import { SoloLettura, euro, usaModifica } from './comune.js';

const CHIAVE = ['tariffe-lezioni'];

const NOME_DURATA: Readonly<Record<number, string>> = { 60: '1 ora', 90: '1 ora e mezza' };

const NOMI_PERSONE = ['Singola', 'Doppia', 'A tre', 'A quattro'];

/** Cosa dice la linguetta sotto il suo nome. */
const SOTTO_LINGUETTA: Readonly<Record<TipoTariffa, string>> = {
  lezione: '1 ora, da 1 a 4 persone',
  allenamento: '1 ora e mezza, 4 persone',
  corso: 'principiante, intermedio, avanzato',
  tattica: '1 ora e mezza, 3 persone + il maestro, prezzo per maestro',
};

/** «una lezione», «un allenamento», «una partita tattica»: per le frasi della tabella. */
const UN: Readonly<Record<Exclude<TipoTariffa, 'corso'>, string>> = {
  lezione: 'la lezione',
  allenamento: 'l’allenamento',
  tattica: 'la partita tattica',
};

/** «Singola» per la lezione, «Singolo» per l'allenamento: si accorda. La partita tattica: il maestro. */
function nomeRiga(v: VoceTariffa, maestri: readonly MaestroDiSegreteria[]): string {
  if (v.level !== null) return ETICHETTE_LIVELLO[v.level];
  if (v.kind === 'tattica') return maestri.find((m) => m.id === v.instructorId)?.displayName ?? 'Maestro';
  const nome = NOMI_PERSONE[(v.people ?? 1) - 1]!;
  return v.kind === 'allenamento' ? nome.replace(/a$/, 'o') : nome;
}

function chiaveDi(v: VoceTariffa): string {
  return `${v.kind}|${v.people ?? ''}|${v.level ?? ''}|${v.instructorId ?? ''}`;
}

/** 4000 → «40,00»: la cifra dentro la casella, senza simbolo (sta accanto). */
function scritto(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2).replace('.', ',');
}

export function ListinoLezioni(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const qc = useQueryClient();
  const tariffe = useQuery({
    queryKey: CHIAVE,
    queryFn: () => chiama<TariffeLezioni>('GET', '/settings/lesson-rates'),
  });
  // 🆕 20/10/2026: le righe della partita tattica sono i maestri (gli eliminati no).
  const maestri = useQuery({
    queryKey: ['maestri'],
    queryFn: () => chiama<{ items: MaestroDiSegreteria[] }>('GET', '/settings/instructors'),
  });

  const [tipo, setTipo] = useState<TipoTariffa>('lezione');
  /** Quello che si sta battendo in una casella, prima di uscirne. */
  const [bozze, setBozze] = useState<Record<string, string>>({});
  const [stato, setStato] = useState<{ testo: string; fatto: boolean } | null>(null);

  const scrivi = useMutation({
    meta: { fatto: 'tariffa delle lezioni salvata' },
    mutationFn: (p: ScriviTariffa) => chiama<TariffeLezioni>('PUT', '/settings/lesson-rates', p),
    onSuccess: (dopo, p) => {
      qc.setQueryData(CHIAVE, dopo);
      setStato({
        testo:
          p.priceCents === null
            ? 'Svuotata: quel prezzo adesso non è deciso.'
            : `Salvato: ${euro(p.priceCents)}.`,
        fatto: true,
      });
    },
  });

  if (tariffe.error) return <Guasto errore={tariffe.error} />;
  if (maestri.error) return <Guasto errore={maestri.error} />;
  if (!tariffe.data || !maestri.data) return <Attesa cosa="il listino delle lezioni" />;
  const elenco = tariffe.data.items;
  const iMaestri = maestri.data.items;
  const voci = vociDelTipo(
    tipo,
    iMaestri.map((m) => m.id),
  );

  const durata = DURATA_DEL_TIPO[tipo];
  const corso = tipo === 'corso';
  /** Come si legge la cifra: a testa, o al mese a persona. */
  const quota = corso ? 'al mese a persona' : 'a testa';

  function esci(v: VoceTariffa): void {
    const k = chiaveDi(v);
    const battuto = bozze[k];
    if (battuto === undefined) return;
    const togliBozza = (): void =>
      setBozze((b) => {
        const { [k]: _via, ...resto } = b;
        return resto;
      });
    const prima = prezzoDellaVoce(elenco, v);
    const nuovo = battuto.trim() === '' ? null : centesimi(battuto);
    if (battuto.trim() !== '' && nuovo === null) {
      /* ⛔ Una non-cifra non si salva, e la casella torna com'era: meglio
         dirlo che salvare un numero indovinato. */
      setStato({ testo: `«${battuto}» non è una cifra: la casella torna com’era.`, fatto: false });
      togliBozza();
      return;
    }
    togliBozza();
    if (nuovo === prima) return;
    scrivi.mutate({ ...v, priceCents: nuovo } as ScriviTariffa);
  }

  return (
    <section className="pagina-listini listino-lezioni">
      <header className="testata">
        <h2>Listino prezzi lezioni</h2>
        <p className="tenue piccolo">
          Quanto paga <strong>ogni allievo</strong>, per lezione, allenamento, corso o partita tattica. Si
          scrive solo qui: l’app dei maestri lo legge. <strong>Vuota</strong> vuol dire «prezzo non deciso»,
          lo <strong>zero</strong> vuol dire gratis.
        </p>
      </header>
      <SoloLettura mostra={modificabile} />

      <div className="tab-listini" role="tablist" aria-label="Lezioni, allenamenti, corsi o partita tattica">
        {TIPI_TARIFFA.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={t === tipo}
            className={t === tipo ? 'scelto' : ''}
            onClick={() => {
              setTipo(t);
              setStato(null);
            }}
          >
            {ETICHETTE_TIPO_TARIFFA[t]}
            <span className="tenue piccolo">{SOTTO_LINGUETTA[t]}</span>
          </button>
        ))}
      </div>

      <p className="intestazione-tabella">
        In ogni casella quanto paga <strong>ciascun allievo</strong> per{' '}
        {corso
          ? 'un mese di corso di quel livello, con volte da'
          : tipo === 'lezione'
            ? 'una lezione di quel tipo, da'
            : tipo === 'tattica'
              ? 'una partita tattica con quel maestro, da'
              : 'un allenamento, da'}{' '}
        <strong>{NOME_DURATA[durata]}</strong>
        {corso ? (
          <>
            . È la <strong>quota del mese</strong>.
          </>
        ) : (
          '.'
        )}
      </p>

      <div className="tariffe-scorre">
        <table className="tariffe">
          <thead>
            <tr>
              <th />
              <th scope="col">
                {NOME_DURATA[durata]}
                {corso ? ', al mese' : ''}
              </th>
            </tr>
          </thead>
          <tbody>
            {tipo === 'tattica' && voci.length === 0 && (
              <tr>
                <td colSpan={2} className="tenue">
                  I prezzi della partita tattica si scrivono per maestro: si aggiungono da «Maestri».
                </td>
              </tr>
            )}
            {voci.map((v) => {
              const k = chiaveDi(v);
              const prezzo = prezzoDellaVoce(elenco, v);
              const nome = nomeRiga(v, iMaestri);
              const persone = v.people;
              return (
                <tr key={k}>
                  <th scope="row">
                    {nome}
                    {persone !== null && (
                      <small>
                        {tipo === 'tattica' ? 'maestro · ' : ''}
                        {persone === 1 ? '1 persona' : `${persone} persone`}
                      </small>
                    )}
                  </th>
                  <td>
                    <div className="cifra">
                      <input
                        inputMode="decimal"
                        placeholder="–"
                        disabled={!modificabile}
                        aria-label={
                          tipo === 'tattica'
                            ? `Partita tattica con ${nome}, ${NOME_DURATA[durata]}, ${quota}`
                            : `${corso ? 'Corso' : tipo === 'lezione' ? 'Lezione' : 'Allenamento'} ${nome.toLowerCase()}, ${NOME_DURATA[durata]}, ${quota}`
                        }
                        value={bozze[k] ?? scritto(prezzo)}
                        onChange={(e) => setBozze((b) => ({ ...b, [k]: e.target.value }))}
                        onBlur={() => esci(v)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                        }}
                      />
                      <span>€</span>
                    </div>
                    {prezzo === null ? (
                      <small className="manca">non deciso</small>
                    ) : persone !== null && persone > 1 ? (
                      /* ⭐ Il totale sotto la cifra: chi scrive 25 a testa su
                         una doppia vede subito che la lezione incassa 50. Sul
                         corso no — quanti sono cambia di mese in mese. */
                      <small>
                        {quota} · {tipo === 'corso' ? '' : UN[tipo]} {euro(prezzo * persone)}
                      </small>
                    ) : (
                      <small>{quota}</small>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Guasto errore={scrivi.error} />
      <p className={`stato-salva${stato?.fatto ? ' fatto' : ''}`} aria-live="polite">
        {scrivi.isPending
          ? 'Un attimo…'
          : (stato?.testo ?? (modificabile ? 'Le cifre si salvano uscendo dalla casella.' : ''))}
      </p>
    </section>
  );
}
